import React, { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Toaster, toast } from "react-hot-toast";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

function GenericReviewAdd() {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    defaultValues: {
      reviews: [{ comment: "", rating: "", description: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "reviews",
  });

  const [isLoading, setIsLoading] = useState(false);

  // Prefill data when editing
  useEffect(() => {
    if (id) {
      const fetchReview = async () => {
        try {
          const token = localStorage.getItem("vedhvidhi-admin-token");
          const response = await axios.get(
            `${process.env.REACT_APP_BASEURL}/admin/review/${id}`,
            {
              headers: {
                Authorization: `${token}`,
              },
            }
          );
          const reviewData = response?.data?.data?.reviews || [];
          if (reviewData.length > 0) {
            setValue("reviews", reviewData);
          }
        } catch (err) {
          console.error("Error fetching review:", err);
          toast.error("Failed to fetch review data");
        }
      };

      fetchReview();
    }
  }, [id, setValue]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("vedhvidhi-admin-token");
      const sanitizedReviews = data.reviews.map(({ _id, ...rest }) => rest);
      const formattedData = { reviews: sanitizedReviews };

      const response = id
        ? await axios.patch(
            `${process.env.REACT_APP_BASEURL}/admin/review/${id}`,
            formattedData,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `${token}`,
              },
            }
          )
        : await axios.post(
            `${process.env.REACT_APP_BASEURL}/admin/review`,
            formattedData,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `${token}`,
              },
            }
          );

      if (response?.data?.success) {
        toast.success(response?.data?.message || "Review saved successfully!");
        navigate("/generic-reviews");
      } else {
        toast.error("Review not saved");
      }
    } catch (error) {
      console.error("Error saving Review:", error);
      toast.error("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={true} />
      <div className="card-body">
      <button
                onClick={() => window.history.back()}
                style={{
                    marginBottom: "10px",
                    backgroundColor: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "monospace",
                    fontSize: "18px",
                    color: "#007bff",
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                    padding: "10px 20px",
                }}
            >
                <span style={{ marginRight: "8px" }}>&lt;</span>
                <span style={{ position: "relative", display: "inline-block" }}>
                    Back
                    <span
                        style={{
                            content: "''",
                            position: "absolute",
                            left: 0,
                            bottom: -2,
                            width: "100%",
                            height: "1px",
                            borderBottom: "2px solid #007bff",
                        }}
                    ></span>
                </span>
            </button>
        <h2>{id ? "Edit Generic Review" : "Add Generic Review"}</h2>
        <form className="row g-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="col-md-12">
            {fields.map((item, index) => (
              <div key={item.id} className="mb-4 border rounded p-3">
                <label className="form-label">Comment</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter your comment"
                  {...register(`reviews[${index}].comment`, {
                    required: "Comment is required",
                  })}
                />
                {errors.reviews?.[index]?.comment && (
                  <span className="text-danger">
                    {errors.reviews[index].comment.message}
                  </span>
                )}

                <label className="form-label mt-3">Rating</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter rating (1 to 5)"
                  {...register(`reviews[${index}].rating`, {
                    required: "Rating is required",
                    min: { value: 1, message: "Minimum rating is 1" },
                    max: { value: 5, message: "Maximum rating is 5" },
                  })}
                />
                {errors.reviews?.[index]?.rating && (
                  <span className="text-danger">
                    {errors.reviews[index].rating.message}
                  </span>
                )}

                <label className="form-label mt-3">Description</label>
                <textarea
                  className="form-control"
                  rows="5"
                  placeholder="Enter description"
                  {...register(`reviews[${index}].description`, {
                    required: "Description is required",
                  })}
                />
                {errors.reviews?.[index]?.description && (
                  <span className="text-danger">
                    {errors.reviews[index].description.message}
                  </span>
                )}

                {fields.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger mt-3"
                    onClick={() => remove(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              className="btn btn-primary mt-2"
              onClick={() => append({ comment: "", rating: "", description: "" })}
            >
              Add Review
            </button>
          </div>

          <div className="col-12 mt-4">
            <button
              type="submit"
              className="btn btn-primary text-uppercase px-3"
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default GenericReviewAdd;
